// HT-4 · 学習の適用状況（有効にした学習内容が実際にどう使われたか）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Panel, Pill, Empty, Collapse } from './shared';

export default function ImpactReview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/impact-review`, { headers: authHeader() });
      setD(r.data);
    } catch (e) {
      setErr(detailOf(e, '適用状況を取得できませんでした'));
      toast.error(detailOf(e, '適用状況を取得できませんでした'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <Panel title="学習の適用状況" testId="learn-impact-review">
        <p className="text-[11.5px] text-amber-900" data-testid="impact-error">{err}</p>
      </Panel>
    );
  }
  if (!d) return null;
  return (
    <Panel title="学習の適用状況" testId="learn-impact-review"
      hint="有効にした学習内容が、実際の読み取りでどう使われたかの記録です。過去の書類は自動で書き換えません。">
      <p className="flex flex-wrap gap-1.5" data-testid="impact-summary">
        <Pill tone={d.active ? 'teal' : 'zinc'} testId="impact-version">
          {d.active ? `バージョン ${d.learning_version}` : '有効な学習内容なし'}
        </Pill>
        <Pill>使われた書類 {d.document_count}件</Pill>
        <Pill tone={d.unused_rule_count ? 'amber' : 'teal'}>
          まだ使われていない学習内容 {d.unused_rule_count}件
        </Pill>
        <Pill tone={(d.auto_confirmed_fields || []).length ? 'rose' : 'teal'}
          testId="impact-auto-confirm">
          自動確定 {(d.auto_confirmed_fields || []).length}件
        </Pill>
        {(d.activated_profiles_ja || []).length > 0 && (
          <Pill>対象 {d.activated_profiles_ja.join(' / ')}</Pill>
        )}
        {(d.blocked_profiles_ja || []).length > 0 && (
          <Pill tone="amber">未検証のため対象外 {d.blocked_profiles_ja.join(' / ')}</Pill>
        )}
      </p>

      {(d.rules || []).length === 0 ? (
        <div className="pt-3">
          <Empty testId="impact-empty">
            有効な学習内容がないため、適用の記録はありません。
          </Empty>
        </div>
      ) : (
        <div className="space-y-1.5 pt-3">
          {d.rules.map((r) => (
            <div key={r.rule_id} className="rounded-md border border-zinc-200 px-3 py-2"
              data-testid={`impact-rule-${r.rule_id}`}>
              <p className="flex flex-wrap items-center gap-1.5">
                <Pill>{r.kind_label_ja}</Pill>
                {r.target_ja && r.target_ja !== '—' && (
                  <Pill testId={`impact-target-${r.rule_id}`}>{r.target_ja}</Pill>
                )}
                {r.field_label_ja && r.field_label_ja !== '—' && (
                  <Pill>{r.field_label_ja}</Pill>
                )}
                {r.duplicate_count > 1 && (
                  <Pill tone="amber">同じ内容の承認 {r.duplicate_count}件</Pill>
                )}
                <Pill tone={r.document_count ? 'teal' : 'amber'}>{r.status_ja}</Pill>
                <Pill>使われた書類 {r.document_count}件</Pill>
                <Pill tone={r.final_risk === 'high' ? 'rose'
                  : r.final_risk === 'medium' ? 'amber' : 'teal'}>
                  影響の大きさ {r.final_risk === 'high' ? '大' : r.final_risk === 'medium' ? '中' : '小'}
                </Pill>
                {(r.profiles_ja || []).length > 0 && <Pill>{r.profiles_ja.join(' / ')}</Pill>}
              </p>
              <p className="pt-1 text-[11.5px] text-zinc-700"
                data-testid={`impact-explain-${r.rule_id}`}>{r.explanation_ja}</p>
            </div>
          ))}
        </div>
      )}

      <Collapse label="書類ごとの適用記録" testId="impact-documents">
        {(d.documents || []).length === 0 ? (
          <p className="text-[11.5px] text-zinc-500">
            まだ、この学習内容で読み取った書類はありません。
          </p>
        ) : (
          (d.documents || []).map((x) => (
            <p key={x.document_id} className="text-[11px] text-zinc-600"
              data-testid={`impact-doc-${x.document_id}`}>
              {x.title || x.document_id} · 学習 v{x.learning_version ?? '—'} ·
              {` 使用 ${x.applied_count}件 · `}
              {(x.effects_ja || []).join('・') || '使用なし'}
              {x.read_at ? ` · ${String(x.read_at).slice(0, 10)} ${String(x.read_at).slice(11, 16)}` : ''}
            </p>
          ))
        )}
      </Collapse>

      <ul className="list-disc pl-5 pt-2 text-[11px] text-zinc-500"
        data-testid="impact-guarantees">
        {(d.guarantees_ja || []).map((t) => <li key={t}>{t}</li>)}
      </ul>
    </Panel>
  );
}
